import { useCallback, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './index';
import { TermTabType } from '../constants';
import { setSelectedFundingTermIndex } from '../../features/global/globalSlice';
import { AdviceGoalPayloadType } from '../../features/client-goals';

type GoalField = AdviceGoalPayloadType['formFields'][0];

const TERM_TABS: TermTabType[] = [TermTabType.SHORT, TermTabType.MEDIUM, TermTabType.LONG];

/**
 * Owns the Funding Navigator term tab (Short/Medium/Long) purely for tab purposes.
 *
 * The selected tab lives in redux (`global.selectedFundingTermIndex`) and only ever changes
 * on an explicit user click or a genuine goal change (add/edit/delete). It deliberately does
 * NOT subscribe to `ewsResponses`, so streamed EWS updates never reset the tab back to Short.
 * The auto-landing effect depends only on the stable `formFields` reference and is further
 * guarded by a goals signature so unrelated global-state changes can't override a user's tab.
 */
export const useFundingNavigatorTabs = () => {
    const dispatch = useAppDispatch();
    const formFields = useAppSelector((state) => state.adviceGoals?.formFields);
    const selectedTermIndex = useAppSelector((state) => state.global?.selectedFundingTermIndex ?? TermTabType.SHORT);

    const goalCountsByTerm = useMemo(() => {
        return TERM_TABS.reduce((acc, term) => {
            acc[term] = (formFields ?? []).filter((goal: GoalField) => term === goal.data.goalCategoryOrder - 1).length;
            return acc;
        }, {} as Record<TermTabType, number>);
    }, [formFields]);

    const goalsSignature = useMemo(() => {
        return (formFields ?? [])
            .map(
                (g: GoalField) =>
                    `${(g as any).goalId}:${g.data.goalCategoryOrder}:${g.data.updatedAt ?? g.data.createdAt}`
            )
            .join('|');
    }, [formFields]);

    const lastAppliedSignatureRef = useRef<string | null>(null);

    const handleGoalTermTabChange = useCallback(
        (_event: React.SyntheticEvent<Element, Event> | null, newValue: TermTabType) => {
            // Mark the current goal set as settled so a pending/late auto-landing (e.g. after an async update) doesn't override the user's explicit tab choice.
            lastAppliedSignatureRef.current = goalsSignature;
            dispatch(setSelectedFundingTermIndex(newValue));
        },
        [dispatch, goalsSignature]
    );

    return {
        selectedTermIndex,
        handleGoalTermTabChange,
        goalCountsByTerm
    };
};
